import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Paperclip, Network, Plus, Sparkles, Mic, Volume2, VolumeX, Linkedin, UserCheck, Target } from "lucide-react";
import { MentisMark } from "@/components/MentisMark";

const Bubble = ({ role, content }) => {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`chat-msg-${role}`}
    >
      {!isUser && <div className="shrink-0 mt-0.5"><MentisMark size={26} /></div>}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-line ${
        isUser
          ? "bg-orange-500/12 border border-orange-500/25 text-slate-900"
          : "bg-black/[0.03] border border-black/[0.08] text-slate-800"
      }`}>
        {content}
      </div>
    </motion.div>
  );
};

/* ------------------------------------------------------------- contextual UI */
function ContextUI({ ui, sending, handlers }) {
  const [chips, setChips] = useState([]);
  const cvRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "" });
  const [linked, setLinked] = useState("");
  const [preview, setPreview] = useState({ name: "", current_role: "", industry: "", years_experience: "" });

  useEffect(() => {
    if (ui?.kind === "profile_preview") {
      const p = ui.profile || {};
      setPreview({
        name: p.name || "",
        current_role: p.current_role || "",
        industry: p.industry || "",
        years_experience: p.years_experience ?? "",
      });
    }
  }, [ui]);

  if (!ui || sending) return null;

  if (ui.kind === "profile_preview") {
    const setP = (k) => (e) => setPreview((p) => ({ ...p, [k]: e.target.value }));
    const fields = [
      ["name", "Full name"], ["current_role", "Current role"],
      ["industry", "Industry"], ["years_experience", "Years of experience"],
    ];
    return (
      <div className="mt-4 rounded-2xl p-4 bg-black/[0.03] border border-black/10" data-testid="profile-preview-ui">
        <div className="mono-label text-orange-600 text-[9px] mb-3 flex items-center gap-1.5">
          <UserCheck size={11} /> {ui.source || "What I pulled from your profile"}
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {fields.map(([k, ph]) => (
            <div key={k} className={k === "name" ? "col-span-2" : ""}>
              <label className="text-[10px] text-slate-700 mb-1 block">{ph}</label>
              <input data-testid={`preview-${k}`} value={preview[k]} onChange={setP(k)} placeholder={ph}
                className="w-full rounded-lg px-3 py-2 bg-black/[0.03] border border-black/10 text-[13px] text-slate-900 placeholder:text-slate-400 focus:border-orange-500/50" />
            </div>
          ))}
        </div>

        {ui.strengths?.length > 0 && (
          <div className="mt-4" data-testid="preview-strengths">
            <div className="mono-label text-slate-700 text-[9px] mb-2">Strengths I inferred</div>
            <div className="flex flex-wrap gap-1.5">
              {ui.strengths.map((s) => (
                <span key={s.id} data-testid={`preview-strength-${s.id}`}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-700">
                  {s.name} · {s.score}
                </span>
              ))}
            </div>
          </div>
        )}

        {ui.gaps?.length > 0 && (
          <div className="mt-4" data-testid="preview-gaps">
            <div className="mono-label text-amber-600 text-[9px] mb-2 flex items-center gap-1.5">
              <Target size={11} /> Gaps your degree will close
            </div>
            <div className="flex flex-col gap-1.5">
              {ui.gaps.map((g) => (
                <div key={g.id} data-testid={`preview-gap-${g.id}`}
                  className="rounded-lg px-3 py-2 bg-amber-500/[0.06] border border-amber-500/15">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-slate-900">{g.name}</span>
                    <span className="text-[10px] text-slate-700">{g.school_short}</span>
                  </div>
                  <div className="text-[11px] text-slate-700 mt-0.5 leading-snug">{g.why}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button data-testid="preview-confirm" onClick={() => handlers.onConfirmProfile(preview)}
          className="btn-orange mt-4 rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 w-full justify-center">
          Looks right — continue <Sparkles size={14} />
        </button>
      </div>
    );
  }

  if (ui.kind === "linkedin") {
    return (
      <div className="mt-4 space-y-2.5" data-testid="linkedin-ui">
        <input data-testid="linkedin-url-input" value={linked} onChange={(e) => setLinked(e.target.value)}
          placeholder="https://www.linkedin.com/in/your-profile"
          className="w-full rounded-xl px-4 py-2.5 bg-black/[0.03] border border-black/10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500/50" />
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="linkedin-submit" disabled={!linked.trim()}
            onClick={() => handlers.onLinkedin({ url: linked.trim() })}
            className="btn-orange rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            Connect LinkedIn <Linkedin size={14} />
          </button>
          <span className="text-[11px] text-slate-700">or</span>
          <button data-testid="linkedin-upload-btn" onClick={() => cvRef.current?.click()}
            className="rounded-full px-4 py-2.5 text-sm font-semibold border border-black/15 text-slate-900 hover:bg-black/[0.05] flex items-center gap-2 transition-colors">
            <Paperclip size={14} /> Upload CV / LinkedIn PDF
          </button>
        </div>
        <input ref={cvRef} type="file" accept=".pdf,.docx" className="hidden"
          onChange={(e) => { if (e.target.files[0]) handlers.onUploadCV(e.target.files[0]); e.target.value = ""; }} />
        <div className="text-[11px] text-slate-700">Paste your profile URL or upload a file — I'll compute your profile automatically.</div>
      </div>
    );
  }

  if (ui.kind === "linkedin_fallback") {
    return (
      <div className="mt-4 space-y-2.5" data-testid="linkedin-fallback-ui">
        <textarea data-testid="linkedin-text-input" value={linked} onChange={(e) => setLinked(e.target.value)} rows={4}
          placeholder="Paste your LinkedIn headline, About and experience here…"
          className="w-full rounded-xl px-4 py-2.5 bg-black/[0.03] border border-black/10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500/50 resize-none scroll-thin" />
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="linkedin-text-submit" disabled={!linked.trim()}
            onClick={() => handlers.onLinkedin({ text: linked.trim() })}
            className="btn-orange rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            Build my profile
          </button>
          <span className="text-[11px] text-slate-700">or</span>
          <button data-testid="fallback-upload-btn" onClick={() => cvRef.current?.click()}
            className="rounded-full px-4 py-2.5 text-sm font-semibold border border-black/15 text-slate-900 hover:bg-black/[0.05] flex items-center gap-2 transition-colors">
            <Paperclip size={14} /> Upload CV / LinkedIn PDF
          </button>
        </div>
        <input ref={cvRef} type="file" accept=".pdf,.docx" className="hidden"
          onChange={(e) => { if (e.target.files[0]) handlers.onUploadCV(e.target.files[0]); e.target.value = ""; }} />
      </div>
    );
  }

  if (ui.kind === "chips") {
    const toggle = (id) => setChips((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    return (
      <div className="mt-4" data-testid="worked-chips">
        <div className="flex flex-wrap gap-2">
          {ui.chips.map((c) => {
            const on = chips.includes(c.id);
            return (
              <button key={c.id} data-testid={`chip-${c.id}`} onClick={() => toggle(c.id)}
                className={`chip rounded-full px-3.5 py-1.5 text-[13px] border ${
                  on ? "bg-orange-500 text-slate-900 border-orange-500 font-semibold"
                     : "bg-black/[0.03] text-slate-700 border-black/10 hover:border-orange-500/40"}`}>
                {c.label}
              </button>
            );
          })}
        </div>
        <button data-testid="worked-continue" onClick={() => handlers.onWorked(chips)}
          className="btn-orange mt-4 rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2">
          Continue <Sparkles size={14} />
        </button>
      </div>
    );
  }

  if (ui.kind === "cta") {
    return (
      <div className="mt-4">
        <button data-testid={`cta-${ui.action}`} onClick={() => handlers.onAction(ui.action)}
          className="btn-orange rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2">
          {ui.label} <ArrowUp size={15} className="rotate-45" />
        </button>
      </div>
    );
  }

  if (ui.kind === "decision") {
    return (
      <div className="mt-4 flex flex-wrap gap-2.5" data-testid="decision-ui">
        <button data-testid="decision-keep" onClick={() => handlers.onDecision("keep", ui.comp)}
          className="btn-orange rounded-full px-5 py-2 text-sm font-semibold">{ui.keep_label}</button>
        <button data-testid="decision-remove" onClick={() => handlers.onDecision("remove_anyway", ui.comp)}
          className="rounded-full px-5 py-2 text-sm font-medium border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors">
          {ui.remove_label}
        </button>
      </div>
    );
  }

  if (ui.kind === "replace_options") {
    return (
      <div className="mt-4" data-testid="replace-options">
        <div className="mono-label text-slate-700 text-[9px] mb-2">Suggested replacements</div>
        <div className="flex flex-col gap-2">
          {ui.options.map((o) => (
            <button key={o.id} data-testid={`replace-option-${o.id}`}
              onClick={() => handlers.onReplace(ui.removed, o.id)}
              className="flex items-center justify-between rounded-xl px-4 py-2.5 bg-black/[0.03] border border-black/10 hover:border-orange-500/40 transition-colors text-left">
              <span className="text-sm text-slate-900">{o.name}</span>
              <span className="flex items-center gap-2 text-[11px] text-slate-700">{o.school_short}<Plus size={14} className="text-orange-400" /></span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (ui.kind === "recommendation") {
    const c = ui.comp;
    return (
      <div className="mt-4 rounded-xl p-4 bg-orange-500/[0.06] border border-orange-500/20" data-testid="recommendation-ui">
        {c.cross_school && <div className="mono-label text-orange-400 text-[9px] mb-2 flex items-center gap-1.5"><Network size={11} />Cross-school competency</div>}
        <div className="font-display font-bold text-slate-900">{c.name}</div>
        <div className="text-[11px] text-slate-700 mt-0.5">{c.school_name}</div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {c.concepts.slice(0, 4).map((x) => <span key={x} className="text-[10px] px-2 py-0.5 rounded bg-black/[0.04] text-slate-700">{x}</span>)}
        </div>
        <button data-testid="recommendation-add" onClick={() => handlers.onAdd(c.id)}
          className="btn-orange mt-4 rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5">
          <Plus size={14} /> Add to pathway
        </button>
      </div>
    );
  }

  if (ui.kind === "application_form") {
    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    return (
      <div className="mt-4 space-y-2.5" data-testid="application-form">
        {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["location", "Location"]].map(([k, ph]) => (
          <input key={k} data-testid={`app-${k}`} value={form[k]} onChange={set(k)} placeholder={ph}
            className="w-full rounded-xl px-4 py-2.5 bg-black/[0.03] border border-black/10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-500/50" />
        ))}
        <button data-testid="app-submit" onClick={() => handlers.onSubmitApp(form)}
          className="btn-orange rounded-full px-5 py-2.5 text-sm font-semibold w-full">Submit application</button>
      </div>
    );
  }

  if (ui.kind === "pathway_actions") {
    return (
      <div className="mt-4">
        <button data-testid={`cta-${ui.match_action}`} onClick={() => handlers.onAction(ui.match_action)}
          className="rounded-full px-5 py-2 text-sm font-medium border border-orange-500/40 text-orange-700 hover:bg-orange-500/10 transition-colors">
          Match me to a program
        </button>
      </div>
    );
  }

  return null;
}

const THINK_STEPS = {
  profile: ["Reading your profile", "Mapping your capabilities", "Finding your gaps"],
  assessment: ["Reviewing your answer", "Updating your capability map"],
  pathway: ["Recalculating your pathway", "Applying academic rules"],
  default: ["Thinking"],
};

function ThinkingIndicator({ phase }) {
  const steps =
    phase === "linkedin" || phase === "preview" ? THINK_STEPS.profile
      : phase === "assessment" ? THINK_STEPS.assessment
      : ["pathway", "modify", "match"].includes(phase) ? THINK_STEPS.pathway
        : THINK_STEPS.default;
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (steps.length <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % steps.length), 1300);
    return () => clearInterval(t);
  }, [phase]); // eslint-disable-line
  return (
    <div className="flex gap-3" data-testid="thinking-indicator">
      <MentisMark size={26} />
      <div className="rounded-2xl px-4 py-3 bg-black/[0.03] border border-black/[0.08] min-w-[220px]">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center gap-1.5">
            <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
            <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
            <span className="dot w-1.5 h-1.5 rounded-full bg-orange-400" />
          </span>
          <span key={i} className="text-[13px] text-slate-700 mentis-fade" data-testid="thinking-text">
            {steps[i]}…
          </span>
        </div>
        <div className="mt-2.5 h-[3px] w-full rounded-full bg-black/[0.05] overflow-hidden" data-testid="thinking-progress">
          <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-orange-500/40 via-orange-400 to-orange-500/40 mentis-progress" />
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Chat panel */
export default function ChatPanel({ session, sending, handlers, autoSpeak = false }) {
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [speak, setSpeak] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);
  const spokenRef = useRef(0);
  const effectiveSpeak = speak || autoSpeak;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session.messages, sending, session.ui]);

  // Mentis speaks its latest reply when voice output is on (manual toggle or present mode)
  useEffect(() => {
    if (!effectiveSpeak || !window.speechSynthesis) return;
    const msgs = session.messages || [];
    for (let i = spokenRef.current; i < msgs.length; i++) {
      if (msgs[i].role === "mentis") {
        const u = new SpeechSynthesisUtterance(msgs[i].content);
        u.rate = 1.02; u.pitch = 1;
        window.speechSynthesis.speak(u);
      }
    }
    spokenRef.current = msgs.length;
  }, [session.messages, effectiveSpeak]);

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input isn't supported in this browser."); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = true; rec.continuous = false;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else interim += t;
      }
      setText((finalText + interim).trim());
    };
    rec.onend = () => {
      setListening(false);
      const t = finalText.trim();
      if (t) { setText(""); handlers.onSend(t); }
    };
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };

  const toggleMic = () => {
    if (listening) { recognitionRef.current?.stop(); setListening(false); }
    else startListening();
  };

  const send = () => {
    const t = text.trim();
    if (!t || sending) return;
    setText("");
    handlers.onSend(t);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const assessmentActive = session.assessment?.active;
  const placeholder = assessmentActive
    ? "Answer Mentis in your own words…"
    : session.phase === "linkedin"
      ? "Paste your LinkedIn URL…"
      : session.phase === "pathway" || session.phase === "modify" || session.phase === "match"
        ? "Talk to your pathway — e.g. \"remove branding\"…"
        : "Message Mentis…";

  return (
    <div className="flex flex-col h-full panel-left" data-testid="chat-panel">
      <div className="px-5 py-4 border-b border-black/[0.08] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <MentisMark size={30} pulse={sending || listening} />
          <div>
            <div className="font-display font-bold text-slate-900 text-[15px] leading-none">Mentis</div>
            <div className="mono-label text-[9px] text-slate-700 mt-1">Operating intelligence</div>
          </div>
        </div>
        <button data-testid="voice-speak-toggle" onClick={() => setSpeak((s) => !s)}
          title={effectiveSpeak ? "Mentis voice on" : "Mentis voice off"}
          className={`p-2 rounded-full border transition-colors ${effectiveSpeak ? "border-orange-500/40 text-orange-400" : "border-black/10 text-slate-700 hover:text-slate-900"}`}>
          {effectiveSpeak ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin px-5 py-6 space-y-5" data-testid="chat-messages">
        {session.messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} />)}
        {sending && <ThinkingIndicator phase={session.phase} />}
        <ContextUI ui={session.ui} sending={sending} handlers={handlers} />
      </div>

      <div className="px-5 py-4 border-t border-black/[0.08]">
        <div className="surface rounded-2xl p-2 flex items-end gap-2">
          <button data-testid="cv-upload-btn" onClick={() => fileRef.current?.click()}
            className="p-2 text-slate-700 hover:text-orange-400 transition-colors" title="Upload CV">
            <Paperclip size={18} />
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden"
            data-testid="cv-file-input"
            onChange={(e) => { if (e.target.files[0]) handlers.onUploadCV(e.target.files[0]); e.target.value = ""; }} />
          <button data-testid="voice-mic-btn" onClick={toggleMic}
            className={`p-2 transition-colors ${listening ? "text-orange-400" : "text-slate-700 hover:text-orange-400"}`}
            title="Speak to Mentis">
            <Mic size={18} className={listening ? "animate-pulse" : ""} />
          </button>
          <textarea
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder={placeholder}
            className="flex-1 bg-transparent resize-none text-[14px] text-slate-900 placeholder:text-slate-400 py-2 max-h-32 scroll-thin"
          />
          <button data-testid="chat-send" onClick={send} disabled={!text.trim() || sending}
            className="btn-orange rounded-xl p-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowUp size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
